import { PRODUCT_COLOR } from './chartTheme';

/**
 * The three markets a device can hold a registered (prequalified) capacity for, with the
 * page-wide product colours. These are the capacities Emulate ops register after
 * prequalification — the platform's record of what may be offered per market, not a
 * measurement.
 */
export const CAPACITY_MARKETS = [
    { key: 'fcr_n', label: 'FCR-N', color: PRODUCT_COLOR.fcrN },
    { key: 'fcr_d_up', label: 'FCR-D up', color: PRODUCT_COLOR.fcrDUp },
    { key: 'fcr_d_down', label: 'FCR-D down', color: PRODUCT_COLOR.fcrDDown },
];

/**
 * A registered capacity (MW) as the page prints it. Zero and null both read "Not registered":
 * the source stores a 0 for a market a device is not prequalified for and has no row at all
 * for a device nothing was ever registered on — neither is a capacity. Sub-MW figures read in
 * kW (the typical per-device magnitude, e.g. 0.05 MW → 50 kW), 1 MW and up in MW.
 *
 * @param {number|null|undefined} mw
 */
export const fmtCapacityMw = mw => {
    if (typeof mw !== 'number' || !Number.isFinite(mw) || mw === 0) {
        return 'Not registered';
    }
    return mw < 1 ? `${+(mw * 1000).toFixed(1)} kW` : `${+mw.toFixed(2)} MW`;
};

/**
 * The battery's registered capacity per market: the sum of its members' registered
 * capacities, MW — the same aggregation the platform's own indicators use. Null when no
 * member row carries the field at all (a gateway from before it existed), so the caller can
 * hide the table rather than show a wall of false "Not registered".
 *
 * @param {Array<{marketCapacities: {fcr_n: number|null, fcr_d_up: number|null, fcr_d_down: number|null}|null}>} members
 * @returns {{fcr_n: number, fcr_d_up: number, fcr_d_down: number}|null}
 */
export const sumMemberCapacities = members => {
    const rows = members.map(m => m.marketCapacities).filter(c => c !== null && c !== undefined && typeof c === 'object');
    if (rows.length === 0) {
        return null;
    }
    const total = key => rows.reduce((a, c) => a + (typeof c[key] === 'number' && Number.isFinite(c[key]) ? c[key] : 0), 0);
    return { fcr_n: total('fcr_n'), fcr_d_up: total('fcr_d_up'), fcr_d_down: total('fcr_d_down') };
};
